// src/features/system/iam-admin/components/UserPickerCard.tsx
// ────────────────────────────────────────────────────────────────────────────
// Searchable, paginated-ish user picker. Pure renderer — owns no fetch.
// Receives users + selection from parent (hook-owns-all-state pattern).
// ────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState } from 'react';
import { CARD, TEXT, BADGE } from '@/shared/dsp/cardTokens';
import type { UserPickerEntry } from '../api/iamAdminApi';

export interface UserPickerCardProps {
  users:        UserPickerEntry[];
  selectedRef:  string | null;
  onSelect:     (userRef: string) => void;
  isLoading?:   boolean;
  isError?:     boolean;
  errorMessage?: string;
}

export default function UserPickerCard({
  users, selectedRef, onSelect, isLoading, isError, errorMessage,
}: UserPickerCardProps) {
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(u =>
      u.username.toLowerCase().includes(needle) ||
      (u.fullName ?? '').toLowerCase().includes(needle) ||
      (u.email ?? '').toLowerCase().includes(needle) ||
      String(u.idUser).includes(needle));
  }, [users, q]);

  return (
    <div className={`pac-glass ${CARD.container}`}>
      <div className={CARD.headerSpaced}>
        <div className="flex items-center gap-2">
          <span className={TEXT.primaryBold}>Users</span>
          <span className={`${BADGE.base} ${BADGE.neutral}`} aria-label="user count">
            {users.length}
          </span>
        </div>
      </div>
      <div className={CARD.section}>
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by username, name, email, id…"
          aria-label="Search users"
          className="pac-glass-input w-full rounded px-2 py-1 text-xs text-neutral-200 placeholder-neutral-500 outline-none focus:border-emerald-500/50"
        />
      </div>
      <div className={`${CARD.rowList} max-h-[60vh] overflow-y-auto`}>
        {isLoading && (
          <div className={`${CARD.section} ${TEXT.secondary}`}>Loading users…</div>
        )}
        {isError && (
          <div className={`${CARD.section} text-xs text-red-400`}>
            Failed to load users{errorMessage ? `: ${errorMessage}` : ''}.
          </div>
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <div className={`${CARD.section} ${TEXT.emptyState}`}>No users match.</div>
        )}
        {filtered.map(u => {
          const isSelected = u.userRef === selectedRef;
          const canSelect = typeof u.userRef === 'string' && u.userRef.length > 0;
          return (
            <button
              key={u.userRef || `user-${u.idUser}`}
              type="button"
              onClick={() => canSelect && onSelect(u.userRef)}
              disabled={!canSelect}
              className={`w-full text-left ${CARD.section} ${CARD.rowHover} ${
                isSelected ? 'bg-emerald-500/[0.06] border-l-2 border-emerald-500/60' : ''
              }`}
              aria-pressed={isSelected}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className={`${TEXT.primary} truncate`}>{u.username}</div>
                  {u.fullName ? (
                    <div className={`${TEXT.secondary} truncate`}>{u.fullName}</div>
                  ) : null}
                  {u.email ? (
                    <div className={`${TEXT.muted} truncate`}>{u.email}</div>
                  ) : null}
                </div>
                <div className="flex flex-col items-end gap-0.5 shrink-0">
                  <span className={`${BADGE.base} ${BADGE.neutral}`}>#{u.idUser}</span>
                  {u.status ? (
                    <span className={`${BADGE.base} ${u.status === 'active' ? BADGE.success : BADGE.neutral}`}>
                      {u.status}
                    </span>
                  ) : null}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
